import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Compact workload summary.
 *
 * Was six cards (four counters, a progress bar and "Avg Review Time") occupying
 * the top of the page. A reviewer opens this screen to answer one question —
 * *what do I review next* — and none of those numbers answer it; they were
 * pushing the queue below the fold.
 *
 * Now a single quiet strip that sits UNDER the queue as context. Overdue is the
 * only figure allowed to raise its voice, and only when it is non-zero, since
 * it is the only one that implies an action.
 */
const WorkloadStats = ({ stats }) => {
  const { t } = useTranslation('dashboard');
  const k = (s, o) => t(`reviewerDashboard.workload.${s}`, o);

  const overdue = stats?.overdue || 0;
  const rate = Math.max(0, Math.min(100, Number(stats?.completionRate) || 0));

  const figures = [
    { label: k('assigned'), value: stats?.total || 0 },
    { label: k('completed'), value: stats?.completed || 0 },
    { label: k('pending'), value: stats?.pending || 0 },
  ];

  return (
    <section className="mt-6 border-t border-border pt-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {figures.map((f) => (
          <div key={f.label} className="flex items-baseline gap-1.5">
            <span className="text-sm font-semibold text-foreground tnum">{f.value}</span>
            <span className="text-xs text-muted-foreground">{f.label}</span>
          </div>
        ))}

        {/* Only speaks up when there is something to say. */}
        {overdue > 0 && (
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-semibold text-error tnum">{overdue}</span>
            <span className="text-xs text-error">{k('overdue')}</span>
          </div>
        )}

        <div className="flex items-center gap-2 ms-auto min-w-[9rem]">
          <div className="h-1 flex-1 bg-muted overflow-hidden">
            <div
              className="h-full bg-success transition-all duration-500"
              style={{ width: `${rate}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tnum whitespace-nowrap">
            {k('complete', { rate })}
          </span>
        </div>
      </div>
    </section>
  );
};

export default WorkloadStats;
